import { useDeferredValue } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Launch from '@mui/icons-material/Launch';
import { ButtonBase } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import SideDrawer from '@/components/SideDrawer';
import { CsvWidgetExportView } from '@/features/DataHub/components/ExportDrawer/CsvWidgetExportView';
import { CustomChartExportView } from '@/features/DataHub/components/ExportDrawer/CustomChartExportView';
import ExportDrawerEmptyState from '@/features/DataHub/components/ExportDrawer/EmptyState';
import { ExportConfigView } from '@/features/DataHub/components/ExportDrawer/ExportConfigView';
import { RestrictedTableExportView } from '@/features/DataHub/components/ExportDrawer/RestrictedTableExportView';
import { useExportHistory } from '@/features/DataHub/hooks/useExportHistory';
import { useOnClose, useOnOpen } from '@/features/DataHub/hooks/useOnOpen';
import {
  useExportMeta,
  useExportRequest
} from '@/features/DataHub/modules/Export/context/exportContexts';
import { PastExports } from '@/features/DataHub/pages/PastExports';
import { useAppSelector } from '@/hooks/redux-hooks';

interface ExportDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ExportDrawer = ({ open, onClose }: ExportDrawerProps) => {
  const { showHistory, toggleHistory } = useExportHistory();
  const { resetExportState } = useExportRequest();
  const { uniqueSymbols } = useExportMeta();
  const openedFrom = useAppSelector(state => state.drawer.openedFrom);
  const csvWidgetExportContext = useAppSelector(state => state.drawer.csvWidgetExportContext);
  const customChartExportContext = useAppSelector(state => state.drawer.customChartExportContext);
  const restrictedTableExportContext = useAppSelector(
    state => state.drawer.restrictedTableExportContext
  );
  const posthog = usePostHog();

  const hasSymbols = uniqueSymbols.size > 0;

  const deferredHasSymbols = useDeferredValue(hasSymbols);
  const effectiveHasSymbols = open ? hasSymbols : deferredHasSymbols;

  useOnClose(open, resetExportState);

  useOnOpen(open, () => {
    posthog.capture('export_selection_opened', { source: openedFrom });
  });

  const renderContent = () => {
    if (!open) return null;

    if (showHistory) {
      return <PastExports />;
    }

    if (csvWidgetExportContext) {
      return <CsvWidgetExportView />;
    }

    if (customChartExportContext) {
      return <CustomChartExportView />;
    }

    if (restrictedTableExportContext) {
      return <RestrictedTableExportView />;
    }

    if (!effectiveHasSymbols) {
      return <ExportDrawerEmptyState />;
    }

    return <ExportConfigView openKey={open.toString()} />;
  };

  return (
    <SideDrawer
      before={
        showHistory ? (
          <ButtonBase
            disableRipple
            disableTouchRipple
            sx={{
              px: 2,
              height: '100%',
              backgroundColor: 'background.paper'
            }}
            onClick={toggleHistory}
          >
            <ArrowBackIcon />
          </ButtonBase>
        ) : undefined
      }
      icon={
        <Launch
          sx={{
            fontSize: 32
          }}
        />
      }
      isHistoryActive={showHistory}
      open={open}
      title='Export'
      onClose={onClose}
      onHistoryClick={toggleHistory}
    >
      {renderContent()}
    </SideDrawer>
  );
};

export default ExportDrawer;
